/**
 * InspectPage — one paste box for "what is this payload?".
 *
 * GAM Explainer, ORTB Lint, VAST Inspector and Diff were four Tools entries
 * that all start the same way: someone pastes a payload. This page owns that
 * one paste box, detects the payload kind (utils/detectPayloadKind.js) and
 * switches to the matching tab; the tools render unchanged below it in their
 * `embedded` mode. Compare puts the pasted text on the left of the diff.
 *
 * The pasted text is deliberately NOT in the URL — payloads are large and can
 * carry device IDs. Only the tab is (?tab=), so the old /explain, /ortb-lint,
 * /vast and /diff routes redirect to the right tab.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import GamExplainerPage from './GamExplainerPage';
import OrtbLintPage from './OrtbLintPage';
import VastInspectorPage from './VastInspectorPage';
import { DiffToolCore } from '../components/DiffTool';
import { detectPayloadKind, PAYLOAD_KIND_LABEL } from '../utils/detectPayloadKind';

export const INSPECT_TABS = ['gam', 'ortb', 'vast', 'diff'];

const TAB_LABELS = { gam: 'GAM request', ortb: 'OpenRTB', vast: 'VAST', diff: 'Compare' };

/** tabFromSearch(search) — the ?tab= deep link, or 'gam' when absent/unknown. */
export function tabFromSearch(search) {
  try {
    const t = new URLSearchParams(search || '').get('tab');
    return INSPECT_TABS.includes(t) ? t : 'gam';
  } catch { return 'gam'; }
}

const textareaStyle = {
  width: '100%', minHeight: 140, boxSizing: 'border-box', resize: 'vertical',
  padding: 10, borderRadius: 8, border: '1px solid var(--line)',
  background: 'var(--card)', color: 'var(--ink)',
  fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: 1.5,
};

export default function InspectPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => tabFromSearch(location.search));
  const [text, setText] = useState('');
  const [compareWith, setCompareWith] = useState('');

  useEffect(() => {
    if (new URLSearchParams(location.search).has('tab')) setTab(tabFromSearch(location.search));
  }, [location.search]);

  function changeTab(next) {
    setTab(next);
    navigate(`/inspect?tab=${next}`, { replace: true });
  }

  // Follow the payload: when what's pasted changes KIND, jump to its tab.
  // Keyed on the kind, not the text, so editing a URL in place never yanks
  // the user off a tab they picked by hand — and never off Compare.
  const kind = detectPayloadKind(text);
  const lastKind = useRef(kind);
  useEffect(() => {
    if (kind && kind !== lastKind.current && tab !== 'diff') changeTab(kind);
    lastKind.current = kind;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <div style={{ padding: 20, display: 'grid', gap: 12, maxWidth: 1200, margin: '0 auto' }}>
      <div>
        <h2 style={{ margin: 0, color: 'var(--ink)' }}>Inspect</h2>
        <p style={{ margin: '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
          Paste a GAM ad-request URL, an OpenRTB request or response, or VAST/VMAP XML. The
          right tool opens on its own; Compare diffs it against a second payload.
        </p>
      </div>

      {tab !== 'diff' && (
        <div style={{ display: 'grid', gap: 6 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'https://securepubads.g.doubleclick.net/gampad/ads?iu=…   ·   {"id": "…", "imp": […]}   ·   <VAST version="4.0">…'}
            style={textareaStyle}
            spellCheck={false}
          />
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {!text.trim()
              ? 'Nothing pasted yet.'
              : kind
                ? <>Detected: <b style={{ color: 'var(--ink)' }}>{PAYLOAD_KIND_LABEL[kind]}</b></>
                : 'Not recognised — pick a tab below to read it as that format.'}
          </div>
        </div>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(e, v) => changeTab(v)} variant="scrollable" scrollButtons="auto">
          {INSPECT_TABS.map((t) => <Tab key={t} value={t} label={TAB_LABELS[t]} />)}
        </Tabs>
      </Box>

      {tab === 'gam' && <GamExplainerPage embedded value={text} />}
      {tab === 'ortb' && <OrtbLintPage embedded value={text} />}
      {tab === 'vast' && <VastInspectorPage embedded value={text} />}
      {tab === 'diff' && (
        <DiffToolCore
          left={text}
          onLeftChange={setText}
          right={compareWith}
          onRightChange={setCompareWith}
          leftLabel="Pasted payload"
          rightLabel="Compare with"
        />
      )}
    </div>
  );
}
